import React from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useT } from "../../i18n";

const IMAGES = {
  "puerto-madero":
    "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "palermo-verde":
    "https://images.unsplash.com/photo-1551361415-69c87624334f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "belgrano-refaccion":
    "https://images.unsplash.com/photo-1679417302546-2c61d4e313f2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  cordoba:
    "https://images.unsplash.com/photo-1716823549236-8b9135f9b5fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export default function Projects() {
  const { t } = useT();
  const items = t("projects.items") || [];
  const labels = t("projects.cardLabels") || {};

  return (
    <section id="projects" className="py-24 md:py-36 bg-[#efece4]" data-testid="projects-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <div className="eyebrow mb-6">{t("projects.sectionLabel")}</div>
            <h2 className="font-serif text-[#1a211c] tracking-tight leading-[1.05] text-[clamp(2rem,5vw,4rem)]">
              {t("projects.title")}
            </h2>
          </div>
          <p className="max-w-md text-[#1a211c]/70 text-[0.98rem] leading-relaxed">
            {t("projects.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5" data-testid="projects-grid">
          {items.map((p, i) => (
            <motion.a
              key={p.id}
              href="#contact"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden rounded-sm bg-[#1a211c] no-tap-highlight ${
                i === 0 ? "md:col-span-4 aspect-[16/11]" : "md:col-span-2 aspect-[4/5]"
              } ${i === 3 ? "md:col-span-4 aspect-[16/9]" : ""} ${
                i === 2 ? "md:col-span-2" : ""
              }`}
              data-testid={`project-card-${p.id}`}
            >
              <img
                src={IMAGES[p.id]}
                alt={p.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a211c]/85 via-[#1a211c]/30 to-transparent" />

              <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-between text-[#f6f4ee]">
                <div className="flex items-center gap-2 text-[0.7rem] font-mono tracking-[0.24em] text-[#c5b293]">
                  <MapPin size={12} />
                  {p.location}
                </div>

                <div>
                  <div className="text-[0.7rem] font-mono tracking-[0.24em] text-[#f6f4ee]/70 mb-2">
                    {p.kind}
                  </div>
                  <div className="font-serif text-3xl md:text-4xl leading-tight max-w-md">
                    {p.name}
                  </div>

                  <div className="mt-5 flex items-center gap-6">
                    <div>
                      <div className="text-[0.68rem] uppercase tracking-[0.22em] text-[#f6f4ee]/55">
                        {labels.status}
                      </div>
                      <div className="text-[0.95rem] mt-0.5">{p.status}</div>
                    </div>
                    <div className="flex-1 max-w-[240px]">
                      <div className="flex items-center justify-between text-[0.68rem] uppercase tracking-[0.22em] text-[#f6f4ee]/55">
                        <span>{labels.progress}</span>
                        <span className="tabular text-[#c5b293]">{p.progress}</span>
                      </div>
                      <div className="mt-2 h-[3px] bg-[#f6f4ee]/15 overflow-hidden">
                        <div
                          className="h-full bg-[#c5b293]"
                          style={{ width: p.progress }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
